import reactStringReplace from "react-string-replace"
import { ContentSection } from "../components/sections/SimpleContent"
import {
  Image,
  ProductInterface as Props,
  SEO,
  PageSection,
  CartProduct,
  Price,
} from "../ts/interfaces"
import { isCanada, isUSA } from "../utils/Countries"
import { cleanSlug, formatCurrencyString } from "../utils/format"
import {
  getCartProduct,
  getStripeId,
  getCartProducBySku,
} from "../utils/stripejs"
import { isHeadset } from "./shared/headsetUtils"
import { CartItem, Currency } from "./types/checkoutTypes"
import React from "react"
import ProductPriceTiers from "../components/products/ProductPriceTiers"
import { SKU_CONFIG } from "./skuConfig"
export { SKU_CONFIG }

export class Product {
  id: string
  stripeId?: string
  stripeTestId?: string
  slug: string
  seo?: SEO
  name: string
  featuredImage: Image
  gallery: Image[]
  sections: PageSection[]
  subHeading: string
  description: string
  shortDescription: string

  CAD_List_Price: number
  CAD_Discount: number
  USD_List_Price: number
  USD_Discount: number
  outOfStock?: boolean
  outOfStockCountries: string[]
  postPurchaseContent?: ContentSection

  relatedProductPriceLink?: string
  relatedProducts: {
    id: string
  }[]
  canUsOnly?: boolean
  noCoupons?: boolean
  isPro?: boolean
  relatedPriceTxt: string = ""

  stripeProduct: CartProduct | null
  stripeSeatProduct: CartProduct | null = null
  actualstripeId?: string

  productBundles?: {
    product: {
      id: string
    }
    forced: boolean
    title?: string
    description?: string
    skipText?: string
  }[]

  constructor(props: Props) {
    this.id = props.id
    this.stripeId = props.stripeId
    this.stripeTestId = props.stripeTestId
    this.slug = props.slug
    this.seo = props.seo
    this.name = props.name
    this.featuredImage = props.featuredImage
    this.gallery = props.gallery
    this.sections = props.sections
    this.subHeading = props.subHeading
    this.description = props.description
    this.shortDescription = props.shortDescription
    this.CAD_List_Price = props.CAD_List_Price
    this.USD_List_Price = props.USD_List_Price
    this.CAD_Discount = props.CAD_Discount
    this.USD_Discount = props.USD_Discount
    this.outOfStock = props.outOfStock
    this.outOfStockCountries =
      props.outOfStockCountries?.split(",").map(c => c.trim().toLowerCase()) ??
      []
    this.postPurchaseContent = props.postPurchaseContent
    this.relatedProductPriceLink = props.relatedProductPriceLink
    this.relatedProducts = props.relatedProducts || []
    this.canUsOnly = props.canUsOnly
    this.noCoupons = props.noCoupons
    this.isPro = props.isPro
    const stripeId = getStripeId(props.stripeId, props.stripeTestId)
    const stripeProduct = getCartProduct(stripeId)

    if (stripeProduct) {
      props.CAD_List_Price =
        (stripeProduct?.prices?.cad?.unit_amount ?? 0) + props.CAD_Discount
      props.USD_List_Price =
        (stripeProduct?.prices?.usd?.unit_amount ?? 0) + props.USD_Discount
    } else {
      props.CAD_List_Price = props.CAD_Discount
      props.USD_List_Price = props.USD_Discount
    }
    this.stripeProduct = stripeProduct
    this.actualstripeId = stripeId
    this.productBundles = props.productBundles

    const seatSku = this.getSkuConfig().seatsSKU

    if (seatSku) {
      this.stripeSeatProduct = getCartProducBySku(seatSku) ?? null
    }
  }

  isRecurring(): boolean {
    return this.stripeProduct?.prices?.cad?.type === "recurring"
  }

  getSkuConfig(): any {
    if (!!this.stripeProduct?.metadata.SKU) {
      return SKU_CONFIG[this.stripeProduct?.metadata.SKU ?? ""] ?? {}
    }
    return {}
  }

  isPersonalMembership(): boolean {
    return this.isRecurring() && this.getSkuConfig()?.isPersonal === true
  }

  isProfessionalMembership(): boolean {
    return this.isRecurring() && !this.isPersonalMembership()
  }

  getPeriodText(opts: { num: number; titleCase: boolean }): string {
    if (!this.isRecurring()) {
      return ""
    }
    let text = this.isMonthly() ? `${opts.num} Month` : `${opts.num} Year`
    if (opts.num > 1) {
      text += "s"
    }
    return opts.titleCase ? text : text.toLowerCase()
  }

  getProductName(opts: {
    headsetInCart: boolean
    couponDiscountDays: number
    trialDays: number
    grantsLinkedUserMembershipTrialDays?: number
  }): string {
    if (this.isUsage()) {
      return this.name
    }

    if (this.isProfessionalMembership()) {
      return opts.couponDiscountDays > 0 ? this.name + " - Trial" : this.name
    }

    const discountDays = Math.max(
      opts.couponDiscountDays,
      opts.grantsLinkedUserMembershipTrialDays ?? 0
    )
    if (!opts.headsetInCart && discountDays > 0) {
      return `${discountDays} day trial membership`
    }

    const bundledMembership =
      this.isSubscriptionWithTrialDays() && opts.headsetInCart
    if (this.isRecurring() && (bundledMembership || discountDays > 0)) {
      return `${Math.max(
        this.isSubscriptionWithTrialDays() ? opts.trialDays : 0,
        discountDays
      )} day ${
        bundledMembership ? "Trial Membership included" : "trial membership"
      }`
    }
    return this.name
  }

  replaceUsageDescription(str: string, currency: Currency): React.ReactNode[] {
    const amt = this.getPriceAmt(currency)
    let elements: React.ReactNode[] = [str]
    if (amt === null || !this.isUsage()) {
      return elements
    }

    const amtLow =
      this.getPriceAmt(currency, undefined, undefined, "LOW") ?? amt
    const amtHigh =
      this.getPriceAmt(currency, undefined, undefined, "HIGH") ?? amt
    str = str.replace(
      "{UNLIMITED_PERIOD}",
      this.getPeriodText({
        num: this.getUnlimitedNumCycles(),
        titleCase: false,
      })
    )
    elements = [str]

    const replace = ["{PRICE}", "{PRICE_LOW}", "{PRICE_HIGH}"]
    replace.forEach(e => {
      elements = reactStringReplace(elements, e, (match, i, offeset) => {
        let amount = amt
        switch (match) {
          case "{PRICE}":
            amount = amt
            break

          case "{PRICE_LOW}":
            amount = amtLow
            break

          case "{PRICE_HIGH}":
            amount = amtHigh
            break
        }
        return (
          <ProductPriceTiers
            key={match + i}
            displayLinkText={
              formatCurrencyString({ value: amount, currency }, true, false) +
              "*"
            }
            product={this}
          />
        )
      })
    })

    return elements
  }

  getProductBillingDescription(opts: {
    fullDescription: boolean
    currency: Currency
    trialDays: number
    couponDiscountDays: number
    grantsLinkedUserMembershipTrialDays?: number
  }): string | React.JSX.Element {
    const amt = this.getPriceAmt(opts.currency)
    if (amt === null) {
      return ""
    }

    const contract = this.isAnnualContract() ? "Annual Contract" : ""
    if (this.isUsage()) {
      return <>{this.replaceUsageDescription(this.subHeading, opts.currency)}</>
    }
    if (this.hasSeats()) {
      const skuConfig = this.getSkuConfig()
      const seatDesc = opts.fullDescription
        ? skuConfig?.fullSeatDescription ?? "Per Seat"
        : `Per ${skuConfig?.seatNameSingular}` ?? "Per Seat"
      return contract ? `${contract} | ${seatDesc}` : `${seatDesc}`
    }

    if (this.hasUpfrontNumCycles()) {
      const numCycle = this.getUpfrontNumCycles()
      const mobilePre = opts.fullDescription
        ? `${this.getPeriodText({
            num: numCycle,
            titleCase: false,
          })} today then `
        : ""

      const billing = this.isYearly()
        ? `${formatCurrencyString(
            { value: amt / 12, currency: opts.currency },
            true,
            false
          )} per month, billed yearly`
        : `${formatCurrencyString(
            { value: amt, currency: opts.currency },
            true,
            false
          )} per month`

      const post = !opts.fullDescription
        ? ` after ${numCycle > 1 ? `` : "first "}${this.getPeriodText({
            num: numCycle,
            titleCase: false,
          })}`
        : ""

      return mobilePre + billing + post
    }
    if (this.isRecurring()) {
      const billing = this.isYearly()
        ? `${formatCurrencyString(
            { value: amt / 12, currency: opts.currency },
            true,
            false
          )} per month, billed yearly`
        : `${formatCurrencyString(
            { value: amt, currency: opts.currency },
            true,
            false
          )} per month`
      if (
        (opts.trialDays && opts.trialDays > 0) ||
        (opts.couponDiscountDays && opts.couponDiscountDays > 0) ||
        (opts.grantsLinkedUserMembershipTrialDays ?? 0) > 0
      ) {
        return `*${billing} after ${Math.max(
          opts.trialDays,
          opts.couponDiscountDays,
          opts.grantsLinkedUserMembershipTrialDays ?? 0
        )} days`
      }
      return `*${billing}`
    }

    return ""
  }

  isAnnualContract(): boolean {
    return this.getSkuConfig()?.isAnnualContract === true
  }

  isMonthly(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.interval === "month"
  }

  isYearly(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.interval === "year"
  }

  isUsage(): boolean {
    return this.stripeProduct?.prices?.cad?.recurring?.usage_type === "metered"
  }

  hasSeats(): boolean {
    return this.stripeSeatProduct !== null
  }

  isSubscriptionWithTrialDays(): boolean {
    return this.isRecurring() && !!this.stripeProduct?.metadata?.trial_days
  }

  getTrialDays(): number {
    if (!this.isSubscriptionWithTrialDays()) {
      return 0
    }
    return parseInt(this.stripeProduct!.metadata!.trial_days!, 10)
  }

  getMaxPlanMembers(): number {
    if (this.isRecurring()) {
      return parseInt(
        this.stripeProduct?.metadata?.FAMILY_MEMBER_MAX ?? "1",
        10
      )
    }
    return 0
  }

  getUpfrontNumCycles(): number {
    if (!this.isRecurring()) {
      return 1
    }

    const numCycleStr = this.stripeProduct?.metadata?.NUM_PERIOD_UPFRONT ?? "1"
    return parseInt(numCycleStr, 10)
  }

  hasUpfrontNumCycles(): boolean {
    return this.getUpfrontNumCycles() > 1
  }

  getUnlimitedNumCycles(): number {
    if (!this.isUsage()) {
      return 0
    }

    const numCycleStr = this.stripeProduct?.metadata?.NUM_PERIOD_NO_LIMIT ?? "0"
    return parseInt(numCycleStr, 10)
  }

  hasForcedBundle(): boolean {
    return this.productBundles?.some(pb => pb.forced) ?? false
  }

  getForcedProductBundleId(): string | undefined {
    const forced = this.productBundles?.find(pb => pb.forced)
    if (!forced) {
      return
    }
    return forced.product.id
  }

  getDiscount(currency: Currency): number {
    let discount = this.USD_Discount

    if (currency === "cad") {
      discount = this.CAD_Discount
    }
    return discount
  }

  getPriceAmt(
    currency: Currency,
    excludeDiscount = true,
    withUpfront = false,
    tierDisplayMode?: "LOW" | "HIGH"
  ): number | null {
    const price = this.getPrice(currency)

    if (!price) {
      return null
    }
    let unitAmt = price.unit_amount

    if (price.tiers_mode && price.tiers) {
      //tiered pricing --- updated unitAmt
      tierDisplayMode = tierDisplayMode ?? "LOW"
      switch (tierDisplayMode) {
        case "LOW":
          unitAmt = price.tiers[price.tiers.length - 1].unit_amount
          break
        case "HIGH":
          unitAmt = price.tiers[0].unit_amount
          break
      }
    }

    const amt = excludeDiscount ? unitAmt + this.getDiscount(currency) : unitAmt

    return withUpfront ? amt * this.getUpfrontNumCycles() : amt
  }

  getPrice(currency: Currency): Price | null {
    if (!this.stripeProduct) {
      return null
    }
    return this.stripeProduct.prices[currency] ?? null
  }

  getAdditionalSeatPrice(currency: Currency): Price | null {
    if (!this.stripeSeatProduct) {
      return null
    }
    return this.stripeSeatProduct.prices[currency] ?? null
  }

  getAdditionalSeatPriceAmt(currency: Currency): number | null {
    const price = this.getAdditionalSeatPrice(currency)
    return price ? price.unit_amount : null
  }

  getNumIncluded(currency: Currency): number {
    const price = this.getPrice(currency)

    if (!price || !this.isUsage() || !price.tiers) {
      return 0
    }
    return parseInt(
      this.stripeProduct?.metadata?.NUM_PER_PERIOD_INCLUDED ?? "0",
      10
    )
  }

  getPriceTiers(
    currency: Currency
  ): { amount: number; limit?: number; start: number }[] {
    const price = this.getPrice(currency)

    if (!price || !this.isUsage() || !price.tiers) {
      return []
    }
    const incluedAmt = this.getNumIncluded(currency)

    let start = 1
    const res: { amount: number; limit?: number; start: number }[] = []
    if (incluedAmt > 0) {
      //   res.push({ amount: 0, limit: incluedAmt, start })
      start = incluedAmt + 1
    }
    price.tiers.forEach(t => {
      res.push({
        amount: t.unit_amount,
        limit: t.up_to ? t.up_to + incluedAmt : undefined,
        start,
      })
      start = t.up_to ? t.up_to + 1 + incluedAmt : start
    })
    return res
  }

  isHeadset(): boolean {
    return isHeadset(this.stripeProduct)
  }

  isOutOfRegion(country?: string): boolean {
    if (!country) {
      return false
    }

    return this.canUsOnly === true && !isCanada(country) && !isUSA(country)
  }

  isOutOfStock(country?: string): boolean {
    if (this.outOfStock) {
      return true
    }
    if (!country || this.outOfStockCountries.length === 0) {
      return false
    }
    const countryLower = country.toLowerCase()
    return this.outOfStockCountries.includes(countryLower)
  }

  isNoCoupon(): boolean {
    return this.noCoupons === true
  }

  setRelatedPriceText(text: string) {
    this.relatedPriceTxt = text
  }

  getRelatedProduct(
    allProducts: Product[],
    country: string
  ): Product | undefined {
    const hasRelatedPrice =
      this.relatedProductPriceLink && (this.relatedProducts?.length ?? 0) > 0

    const relProd = hasRelatedPrice
      ? allProducts.find(p => p.id === this.relatedProducts![0].id)
      : undefined
    relProd?.setRelatedPriceText(this.relatedProductPriceLink!)
    return relProd?.isOutOfRegion(country) === false ? relProd : undefined
  }

  cleanSlug(): string {
    return cleanSlug(this.slug)
  }

  getQuantity(cartItems: CartItem[]): number {
    let quantity =
      cartItems.find(item => item.productId === this.stripeProduct?.id)
        ?.quantity || 0
    if (this.hasSeats()) {
      quantity =
        quantity +
        (cartItems.find(item => item.productId === this.stripeSeatProduct?.id)
          ?.quantity || 0)
    }
    return quantity
  }
}
